import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { Alert, Box, CircularProgress, Link, Tab, Tabs, Typography } from '@mui/material';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import OfferInventoryPane from './OfferInventoryPane';

interface RoomTypeGroup {
  roomType: App.RoomType;
  propertyId: string;
  vendorId: string;
  roomRates: Array<App.RoomRate>;
}

interface OfferInventoryData {
  offerName: string;
  roomTypeGroups: Array<RoomTypeGroup>;
}

export default function OfferInventoryContainer() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const [selectedTab, setSelectedTab] = useState(0);

  const { data, isLoading, error } = useQuery<OfferInventoryData>({
    queryKey: ['offer-inventory-data', offerId],
    queryFn: async () => {
      const { result: offer } = await OffersService.getOffer(offerId);

      const propertyId: string | undefined = offer?.packages?.[0]?.fk_property_id;
      if (!propertyId) return { offerName: offer.name ?? offerId, roomTypeGroups: [] };

      const propertyResponse = await ReservationService.getProperty(propertyId);
      const property = propertyResponse?.result;

      const offerRoomRateIds = new Set<string>();
      for (const pkg of offer.packages ?? []) {
        for (const option of pkg.package_options ?? []) {
          if (option.fk_room_rate_id) offerRoomRateIds.add(option.fk_room_rate_id);
        }
        if (pkg.fk_room_rate_id) offerRoomRateIds.add(pkg.fk_room_rate_id);
      }

      const vendorId: string = property?.id_salesforce_external ?? '';
      const groupMap = new Map<string, RoomTypeGroup>();
      for (const roomType of property?.room_types ?? []) {
        for (const roomRate of roomType.room_rates ?? []) {
          if (!offerRoomRateIds.has(roomRate.id)) continue;
          if (!groupMap.has(roomType.id)) {
            groupMap.set(roomType.id, { roomType, propertyId, vendorId, roomRates: [] });
          }
          groupMap.get(roomType.id)!.roomRates.push(roomRate);
        }
      }

      return {
        offerName: offer.name ?? offerId,
        roomTypeGroups: Array.from(groupMap.values()),
      };
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box mt={4}>
        <Alert severity="error">Failed to load offer data.</Alert>
      </Box>
    );
  }

  const { offerName, roomTypeGroups } = data;

  if (roomTypeGroups.length === 0) {
    return (
      <Box mt={4}>
        <Typography>No room types found for this offer.</Typography>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Box px={3} mb={1}>
        <Link href={`/offers/${offerId}`} variant="body1" underline="hover">
          ← Back to offer
        </Link>
      </Box>
      <Typography variant="h4" fontWeight={700} mb={3} px={3}>
        Room Inventory — {offerName}
      </Typography>
      <Box display="flex">
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          sx={{ borderRight: 1, borderColor: 'divider', minWidth: 180 }}
        >
          {roomTypeGroups.map((group, i) => (
            <Tab key={group.roomType.id} label={group.roomType.name} value={i} />
          ))}
        </Tabs>
        {roomTypeGroups.map((group, i) => (
          <Box key={group.roomType.id} role="tabpanel" hidden={selectedTab !== i} flex={1} px={3}>
            {selectedTab === i && (
              <>
                <Box mb={2}>
                  <Link
                    href={`/vendors/${group.vendorId}/properties/${group.propertyId}/room-types/${group.roomType.id}/room-rates/${group.roomRates[0]?.id ?? ''}`}
                    variant="body2"
                    underline="hover"
                  >
                    View room rates →
                  </Link>
                </Box>
                <OfferInventoryPane
                  offerId={offerId}
                  propertyId={group.propertyId}
                  roomType={group.roomType}
                />
              </>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
